/**
 * SupportAccessBanner — mounted globally inside Shell.
 * Three modes:
 *  1. Admin is inside an impersonated session -> amber "support session" bar with End button.
 *  2. User has a pending support-access request -> consent modal (Accept / Decline).
 *  3. User has an active session -> slim banner "support is viewing your account" + Revoke.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Eye, ShieldAlert, X, Check, LogOut, NotebookPen, Send } from "lucide-react";
import { toast } from "sonner";

export const endImpersonation = async () => {
  try { await api.post("/support-access/revoke"); } catch { /* session may already be dead */ }
  const adminToken = localStorage.getItem("psc_admin_token");
  const adminUser = localStorage.getItem("psc_admin_user");
  localStorage.removeItem("psc_impersonation");
  localStorage.removeItem("psc_admin_token");
  localStorage.removeItem("psc_admin_user");
  if (adminToken) {
    localStorage.setItem("psc_token", adminToken);
    if (adminUser) localStorage.setItem("psc_user", adminUser);
    window.location.href = "/admin/accounts";
  } else {
    localStorage.removeItem("psc_token");
    localStorage.removeItem("psc_user");
    window.location.href = "/login";
  }
};

const SupportAccessBanner = () => {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const activeRef = useRef(null);
  const imp = localStorage.getItem("psc_impersonation");

  const apply = useCallback((data) => {
    setPending(data.pending);
    if (activeRef.current && !data.active) toast.info("The support session has ended — you have full control again.");
    activeRef.current = data.active;
    setActive(data.active);
  }, []);

  const poll = useCallback(async () => {
    try { apply((await api.get("/support-access/pending")).data); }
    catch { /* ignore */ }
  }, [apply]);

  useEffect(() => {
    if (!user || imp) return;
    let stop = false;
    let ctrl = null;
    // Live push (SSE) — prompt appears the instant support requests access.
    const listen = async () => {
      while (!stop) {
        try {
          ctrl = new AbortController();
          const resp = await fetch(`${API}/support-access/stream`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("psc_token")}` },
            signal: ctrl.signal,
          });
          if (!resp.ok || !resp.body) throw new Error("stream unavailable");
          const reader = resp.body.getReader();
          const dec = new TextDecoder();
          let buf = "";
          for (;;) {
            const { done, value } = await reader.read();
            if (done || stop) break;
            buf += dec.decode(value, { stream: true });
            let i;
            while ((i = buf.indexOf("\n\n")) >= 0) {
              const chunk = buf.slice(0, i);
              buf = buf.slice(i + 2);
              const line = chunk.split("\n").find((l) => l.startsWith("data: "));
              if (line) apply(JSON.parse(line.slice(6)));
            }
          }
        } catch { /* network blip — reconnect below */ }
        if (!stop) await new Promise((r) => setTimeout(r, 5000));
      }
    };
    listen();
    const t = setInterval(poll, 60000); // safety-net fallback
    return () => { stop = true; if (ctrl) ctrl.abort(); clearInterval(t); };
  }, [user, imp, poll, apply]);

  if (!user) return null;

  // ----- mode 1: admin viewing someone else's account -----
  if (imp) {
    const info = JSON.parse(imp);
    return (
      <div>
        <div data-testid="impersonation-bar"
          className="bg-amber-500 text-slate-900 px-4 py-2.5 flex flex-wrap items-center gap-3 border-b-4 border-amber-600">
          <Eye size={16} strokeWidth={2.5} />
          <p className="text-sm font-bold flex-1 min-w-0">
            Support session — you are viewing <span className="underline">{info.name || info.email}</span>&apos;s account.
            Everything you see is live from their account.
          </p>
          <button data-testid="session-notes-toggle-btn" onClick={() => setNotesOpen((o) => !o)}
            className={`h-8 px-3 inline-flex items-center gap-1.5 font-bold text-xs uppercase tracking-wider border-2 border-slate-900 ${
              notesOpen ? "bg-slate-900 text-white" : "bg-amber-400 text-slate-900 hover:bg-amber-300"}`}>
            <NotebookPen size={13} /> Session notes
          </button>
          <button data-testid="impersonation-end-btn" onClick={endImpersonation}
            className="h-8 px-3 inline-flex items-center gap-1.5 bg-slate-900 text-white font-bold text-xs uppercase tracking-wider hover:bg-slate-700">
            <LogOut size={13} /> End session &amp; return to admin
          </button>
        </div>
        {notesOpen && <SessionNotes />}
      </div>
    );
  }

  const respond = async (accept) => {
    setBusy(true);
    try {
      await api.post(`/support-access/${pending.id}/respond`, { accept });
      toast.success(accept ? "Support access granted for up to 60 minutes" : "Request declined");
      setPending(null);
      poll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not respond");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.post("/support-access/revoke");
      toast.success("Support access revoked");
      activeRef.current = null;
      setActive(null);
    } catch { toast.error("Could not revoke"); }
    finally { setBusy(false); }
  };

  return (
    <>
      {/* ----- mode 3: session active on my account ----- */}
      {active && (
        <div data-testid="support-access-active-banner"
          className="bg-[#1E1B4B] text-white px-4 py-2 flex flex-wrap items-center gap-3">
          <Eye size={14} />
          <p className="text-xs font-semibold flex-1 min-w-0">
            Platform support ({active.admin_email}) is currently viewing your account to help you.
          </p>
          <button data-testid="support-access-revoke-btn" onClick={revoke} disabled={busy}
            className="h-7 px-2.5 bg-white text-[#1E1B4B] font-bold text-[11px] uppercase tracking-wider hover:bg-slate-200 disabled:opacity-60">
            Revoke access
          </button>
        </div>
      )}

      {/* ----- mode 2: consent modal ----- */}
      {pending && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 flex items-center justify-center p-4"
          data-testid="support-access-consent-modal">
          <div className="bg-white border-4 border-[#1E1B4B] max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 shrink-0 bg-amber-100 border-2 border-amber-400 flex items-center justify-center">
                <ShieldAlert size={20} className="text-amber-700" />
              </div>
              <div>
                <h2 className="font-black text-slate-900 text-lg leading-tight">Support access request</h2>
                <p className="text-sm text-slate-600 mt-1.5">
                  Platform support (<strong>{pending.admin_email}</strong>) is asking to temporarily view
                  your account to help troubleshoot an issue.
                </p>
                {pending.reason && (
                  <p className="text-xs text-slate-500 mt-2 border-l-2 border-slate-300 pl-2 italic">“{pending.reason}”</p>
                )}
                <ul className="text-xs text-slate-500 mt-3 space-y-1 list-disc pl-4">
                  <li>Access is limited to <strong>60 minutes</strong> and every action is logged.</li>
                  <li>You can revoke access at any time from the banner at the top of your screen.</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button data-testid="support-access-accept-btn" onClick={() => respond(true)} disabled={busy}
                className="flex-1 h-11 inline-flex items-center justify-center gap-2 bg-[#1E1B4B] text-white font-bold text-sm uppercase tracking-wider hover:bg-[#0F0E2E] disabled:opacity-60">
                <Check size={15} /> Allow access
              </button>
              <button data-testid="support-access-decline-btn" onClick={() => respond(false)} disabled={busy}
                className="flex-1 h-11 inline-flex items-center justify-center gap-2 border-2 border-slate-300 text-slate-700 font-bold text-sm uppercase tracking-wider hover:border-red-400 hover:text-red-700 disabled:opacity-60">
                <X size={15} /> Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const SessionNotes = () => {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/support-access/notes").then((r) => setNotes(r.data.notes)).catch(() => {});
  }, []);

  const add = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/support-access/notes", { note: draft.trim() });
      setNotes((n) => [...n, r.data.note]);
      setDraft("");
      toast.success("Note saved to the customer's account history");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="session-notes-panel" className="bg-amber-50 border-b-2 border-amber-300 px-4 py-3">
      <div className="max-w-3xl">
        <div className="flex gap-2">
          <textarea
            data-testid="session-note-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="Jot what you found or fixed — saved permanently to this customer's account history…"
            className="flex-1 px-2.5 py-2 border-2 border-amber-300 bg-white text-sm focus:border-slate-900 focus:outline-none resize-none"
          />
          <button data-testid="session-note-save-btn" onClick={add} disabled={busy || !draft.trim()}
            className="h-auto px-4 inline-flex items-center gap-1.5 bg-slate-900 text-white font-bold text-xs uppercase tracking-wider hover:bg-slate-700 disabled:opacity-50">
            <Send size={13} /> Save note
          </button>
        </div>
        {notes.length > 0 && (
          <ul className="mt-2 space-y-1.5" data-testid="session-notes-list">
            {notes.map((n) => (
              <li key={n.id} className="text-xs text-slate-700 flex gap-2">
                <span className="text-slate-400 whitespace-nowrap">{new Date(n.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                <span>{n.note}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SupportAccessBanner;
